/**
 * Keyword icon mapping for Scryfall keywords (abilities, actions, ability words).
 *
 * Keywords with a mana-font `ms-ability-*` icon (v1.18.0) use it; all others render a
 * generated two-letter glyph badge (see `KeywordAbilityIcon`).
 * Two tiers: **evergreen** (15 universally-known abilities) and **other**.
 */

// ============================================================================
// Types
// ============================================================================

export type KeywordCategory = 'evergreen' | 'other';

export interface KeywordIconDef {
  /** mana-font `ms-ability-*` suffix; when absent, `glyph` is rendered instead */
  icon?: string;
  /** Two-letter fallback glyph: big first letter + subscript second letter */
  glyph: string;
  category: KeywordCategory;
}

export interface KeywordIconEntry extends KeywordIconDef {
  keyword: string;
}

// ============================================================================
// Category Styling Constants
// ============================================================================

/** Evergreen keyword styling — green tint */
export const EVERGREEN_STYLE = {
  /** Inline color for icons (beats unlayered mana-font CSS in cascade) */
  color: '#34d399',
  bg: 'bg-emerald-400/15',
  /** Combined pill style for CardShowcase */
  pill: 'bg-emerald-400/15 text-emerald-400',
} as const;

/** Other keyword styling — neutral amber */
export const OTHER_STYLE = {
  /** Inline color for icons (beats unlayered mana-font CSS in cascade) */
  color: 'rgba(251,191,36,0.7)',
  bg: 'bg-amber-400/10',
  /** Combined pill style for CardShowcase */
  pill: 'bg-amber-400/10 text-amber-400/70',
} as const;

/** Get style constants for a keyword category */
export function getCategoryStyle(category: KeywordCategory) {
  return category === 'evergreen' ? EVERGREEN_STYLE : OTHER_STYLE;
}

// ============================================================================
// Icon Map
// ============================================================================

/** Lowercased Scryfall keywords → mana-font `ms-ability-*` suffixes. */
const MANA_FONT_KEYWORDS: Record<string, { icon: string; category: KeywordCategory }> = {
  // ── Evergreen (15) ──────────────────────────────────────────────────────
  flying: { icon: 'flying', category: 'evergreen' },
  'first strike': { icon: 'first-strike', category: 'evergreen' },
  'double strike': { icon: 'double-strike', category: 'evergreen' },
  deathtouch: { icon: 'deathtouch', category: 'evergreen' },
  defender: { icon: 'defender', category: 'evergreen' },
  flash: { icon: 'flash', category: 'evergreen' },
  haste: { icon: 'haste', category: 'evergreen' },
  hexproof: { icon: 'hexproof', category: 'evergreen' },
  indestructible: { icon: 'indestructible', category: 'evergreen' },
  lifelink: { icon: 'lifelink', category: 'evergreen' },
  menace: { icon: 'menace', category: 'evergreen' },
  reach: { icon: 'reach', category: 'evergreen' },
  trample: { icon: 'trample', category: 'evergreen' },
  vigilance: { icon: 'vigilance', category: 'evergreen' },
  ward: { icon: 'ward', category: 'evergreen' },

  // ── Other keywords with matching mana-font icons ────────────────────────
  adapt: { icon: 'adapt', category: 'other' },
  adamant: { icon: 'adamant', category: 'other' },
  addendum: { icon: 'addendum', category: 'other' },
  adventure: { icon: 'adventure', category: 'other' },
  afflict: { icon: 'afflict', category: 'other' },
  afterlife: { icon: 'afterlife', category: 'other' },
  aftermath: { icon: 'aftermath', category: 'other' },
  alliance: { icon: 'alliance', category: 'other' },
  amass: { icon: 'amass', category: 'other' },
  annihilator: { icon: 'annihilator', category: 'other' },
  ascend: { icon: 'ascend', category: 'other' },
  backup: { icon: 'backup', category: 'other' },
  bargain: { icon: 'bargain', category: 'other' },
  'battle cry': { icon: 'battle-cry', category: 'other' },
  blitz: { icon: 'blitz', category: 'other' },
  boast: { icon: 'boast', category: 'other' },
  casualty: { icon: 'casualty', category: 'other' },
  celebration: { icon: 'celebration', category: 'other' },
  changeling: { icon: 'changeling', category: 'other' },
  channel: { icon: 'channel', category: 'other' },
  cleave: { icon: 'cleave', category: 'other' },
  cloak: { icon: 'cloak', category: 'other' },
  'collect evidence': { icon: 'collect-evidence', category: 'other' },
  companion: { icon: 'companion', category: 'other' },
  constellation: { icon: 'constellation', category: 'other' },
  convoke: { icon: 'convoke', category: 'other' },
  corrupted: { icon: 'corrupted', category: 'other' },
  coven: { icon: 'coven', category: 'other' },
  craft: { icon: 'craft', category: 'other' },
  crew: { icon: 'crew', category: 'other' },
  cycling: { icon: 'cycling', category: 'other' },
  decayed: { icon: 'decayed', category: 'other' },
  delirium: { icon: 'delirium', category: 'other' },
  delve: { icon: 'delve', category: 'other' },
  descend: { icon: 'descend', category: 'other' },
  detain: { icon: 'detain', category: 'other' },
  devotion: { icon: 'devotion', category: 'other' },
  discover: { icon: 'discover', category: 'other' },
  disguise: { icon: 'disguise', category: 'other' },
  disturb: { icon: 'disturb', category: 'other' },
  domain: { icon: 'domain', category: 'other' },
  eerie: { icon: 'eerie', category: 'other' },
  embalm: { icon: 'embalm', category: 'other' },
  enlist: { icon: 'enlist', category: 'other' },
  enrage: { icon: 'enrage', category: 'other' },
  escape: { icon: 'escape', category: 'other' },
  eternalize: { icon: 'eternalize', category: 'other' },
  evolve: { icon: 'evolve', category: 'other' },
  exalted: { icon: 'exalted', category: 'other' },
  expend: { icon: 'expend', category: 'other' },
  exploit: { icon: 'exploit', category: 'other' },
  explore: { icon: 'explore', category: 'other' },
  fabricate: { icon: 'fabricate', category: 'other' },
  fear: { icon: 'fear', category: 'other' },
  ferocious: { icon: 'ferocious', category: 'other' },
  forage: { icon: 'forage', category: 'other' },
  foretell: { icon: 'foretell', category: 'other' },
  gift: { icon: 'gift', category: 'other' },
  goad: { icon: 'goad', category: 'other' },
  hideaway: { icon: 'hideaway', category: 'other' },
  impending: { icon: 'impending', category: 'other' },
  improvise: { icon: 'improvise', category: 'other' },
  incubate: { icon: 'incubate', category: 'other' },
  infect: { icon: 'infect', category: 'other' },
  intimidate: { icon: 'intimidate', category: 'other' },
  investigate: { icon: 'investigate', category: 'other' },
  'jump-start': { icon: 'jumpstart', category: 'other' },
  kicker: { icon: 'kicker', category: 'other' },
  landfall: { icon: 'landfall', category: 'other' },
  learn: { icon: 'learn', category: 'other' },
  magecraft: { icon: 'magecraft', category: 'other' },
  'manifest dread': { icon: 'manifest-dread', category: 'other' },
  mentor: { icon: 'mentor', category: 'other' },
  morph: { icon: 'morph', category: 'other' },
  mutate: { icon: 'mutate', category: 'other' },
  ninjutsu: { icon: 'ninjutsu', category: 'other' },
  offspring: { icon: 'offspring', category: 'other' },
  outlast: { icon: 'outlast', category: 'other' },
  party: { icon: 'party', category: 'other' },
  plot: { icon: 'plot', category: 'other' },
  proliferate: { icon: 'proliferate', category: 'other' },
  protection: { icon: 'protection', category: 'other' },
  prototype: { icon: 'prototype', category: 'other' },
  prowess: { icon: 'prowess', category: 'other' },
  raid: { icon: 'raid', category: 'other' },
  reconfigure: { icon: 'reconfigure', category: 'other' },
  revolt: { icon: 'revolt', category: 'other' },
  riot: { icon: 'riot', category: 'other' },
  saddle: { icon: 'saddle', category: 'other' },
  shroud: { icon: 'shroud', category: 'other' },
  skulk: { icon: 'skulk', category: 'other' },
  spectacle: { icon: 'spectacle', category: 'other' },
  spree: { icon: 'spree', category: 'other' },
  surveil: { icon: 'surveil', category: 'other' },
  survival: { icon: 'survival', category: 'other' },
  suspect: { icon: 'suspect', category: 'other' },
  toxic: { icon: 'toxic', category: 'other' },
  training: { icon: 'training', category: 'other' },
  undergrowth: { icon: 'undergrowth', category: 'other' },
  undying: { icon: 'undying', category: 'other' },
  unearth: { icon: 'unearth', category: 'other' },
  valiant: { icon: 'valiant', category: 'other' },

  // ── Keywords reusing a related mana-font icon ───────────────────────────
  cohort: { icon: 'cohort', category: 'other' },
  compleated: { icon: 'phyrexian', category: 'other' },
  daybound: { icon: 'daybound-nightbound', category: 'other' },
  nightbound: { icon: 'daybound-nightbound', category: 'other' },
  enchant: { icon: 'enchant', category: 'other' },
  fading: { icon: 'fading', category: 'other' },
  'for mirrodin!': { icon: 'for-mirrodin', category: 'other' },
  haunt: { icon: 'haunt', category: 'other' },
  'hexproof from': { icon: 'hexproof', category: 'other' },
  ingest: { icon: 'ingest', category: 'other' },
  meld: { icon: 'meld', category: 'other' },
  monstrosity: { icon: 'monstrous', category: 'other' },
  rally: { icon: 'rally', category: 'other' },
  'read ahead': { icon: 'read-ahead', category: 'other' },
  regenerate: { icon: 'regenerate', category: 'other' },
  renown: { icon: 'renowned', category: 'other' },
  solved: { icon: 'case-solved', category: 'other' },
  soulshift: { icon: 'soulshift', category: 'other' },
  specialize: { icon: 'specialize', category: 'other' },
  transform: { icon: 'transform', category: 'other' },
  'umbra armor': { icon: 'totem-armor', category: 'other' },
  'venture into the dungeon': { icon: 'dungeon', category: 'other' },
  forestwalk: { icon: 'forestwalk', category: 'other' },
  islandwalk: { icon: 'islandwalk', category: 'other' },
  mountainwalk: { icon: 'mountainwalk', category: 'other' },
  plainswalk: { icon: 'plainswalk', category: 'other' },
  swampwalk: { icon: 'swampwalk', category: 'other' },
  megamorph: { icon: 'morph', category: 'other' },
  'commander ninjutsu': { icon: 'ninjutsu', category: 'other' },
  multikicker: { icon: 'kicker', category: 'other' },
  landcycling: { icon: 'cycling', category: 'other' },
  'basic landcycling': { icon: 'cycling', category: 'other' },
  typecycling: { icon: 'cycling', category: 'other' },
  affinitycycling: { icon: 'cycling', category: 'other' },
  forestcycling: { icon: 'cycling', category: 'other' },
  halflingcycling: { icon: 'cycling', category: 'other' },
  islandcycling: { icon: 'cycling', category: 'other' },
  mountaincycling: { icon: 'cycling', category: 'other' },
  plainscycling: { icon: 'cycling', category: 'other' },
  slivercycling: { icon: 'cycling', category: 'other' },
  swampcycling: { icon: 'cycling', category: 'other' },
  wizardcycling: { icon: 'cycling', category: 'other' },
};

/**
 * Scryfall catalog keywords (keyword-abilities, keyword-actions, ability-words) without a
 * mana-font icon. Generic verbs Scryfall never tags on cards (cast, destroy, exile, …) are omitted.
 */
const GLYPH_KEYWORDS: readonly string[] = [
  // Keyword abilities
  'absorb',
  'affinity',
  'amplify',
  'assist',
  'augment',
  'aura swap',
  'awaken',
  'banding',
  'bestow',
  'bloodthirst',
  'buyback',
  'bushido',
  'cascade',
  'champion',
  'choose a background',
  'cipher',
  'conspire',
  'cumulative upkeep',
  'dash',
  'demonstrate',
  'desertwalk',
  'dethrone',
  'devoid',
  'devour',
  "doctor's companion",
  'double agenda',
  'double team',
  'dredge',
  'echo',
  'emerge',
  'encore',
  'entwine',
  'epic',
  'equip',
  'escalate',
  'evoke',
  'exhaust',
  'extort',
  'firebending',
  'flanking',
  'flashback',
  'forecast',
  'fortify',
  'freerunning',
  'frenzy',
  'friends forever',
  'fuse',
  'graft',
  'gravestorm',
  'harmonize',
  'hidden agenda',
  'horsemanship',
  'increment',
  'intensity',
  'job select',
  'landwalk',
  'legendary landwalk',
  'level up',
  'living metal',
  'living weapon',
  'madness',
  'max speed',
  'mayhem',
  'melee',
  'miracle',
  'mobilize',
  'modular',
  'more than meets the eye',
  'myriad',
  'nonbasic landwalk',
  'offering',
  'overload',
  'paradigm',
  'partner',
  'partner with',
  'persist',
  'phasing',
  'poisonous',
  'power-up',
  'provoke',
  'prowl',
  'rampage',
  'ravenous',
  'rebound',
  'recover',
  'reinforce',
  'replicate',
  'retrace',
  'ripple',
  'scavenge',
  'shadow',
  'sneak',
  'soulbond',
  'splice',
  'split second',
  'squad',
  'station',
  'storied',
  'storm',
  'sunburst',
  'surge',
  'suspend',
  'teamwork',
  'tiered',
  'transfigure',
  'transmute',
  'tribute',
  'undaunted',
  'unleash',
  'vanishing',
  'warp',
  'web-slinging',
  'wither',
  // Keyword actions
  'abandon',
  'airbend',
  'assemble',
  'assimilate',
  'behold',
  'blight',
  'bolster',
  'clash',
  'conjure',
  'connive',
  'convert',
  'double',
  'draft from a spellbook',
  'earthbend',
  'endure',
  'exert',
  'face a dilemma',
  'fateseal',
  'fight',
  'food',
  'harness',
  'heal',
  'heist',
  'incorporate',
  'manifest',
  'mill',
  'open an attraction',
  'planeswalk',
  'populate',
  'prepared',
  'recruit',
  'role token',
  'roll to visit your attractions',
  'scry',
  'seek',
  'set in motion',
  'support',
  'time travel',
  'treasure',
  'triple',
  'vote',
  'waterbend',
  // Ability words
  'battalion',
  'bloodrush',
  'chroma',
  'converge',
  "council's dilemma",
  'covercast',
  'disappear',
  'eminence',
  'fateful hour',
  'fathomless descent',
  'flurry',
  'formidable',
  'grandeur',
  'hellbent',
  "hero's reward",
  'heroic',
  'imprint',
  'infusion',
  'inspired',
  'join forces',
  'kinfall',
  'kinship',
  'landship',
  'legacy',
  'lieutenant',
  'metalcraft',
  'morbid',
  'opus',
  'pack tactics',
  'paradox',
  'parley',
  'radiance',
  'renew',
  'repartee',
  'secret council',
  'spell mastery',
  'start your engines!',
  'strive',
  'sweep',
  'tempting offer',
  'threshold',
  'underdog',
  'vivid',
  'void',
  'will of the council',
  'will of the planeswalkers',
];

/** Hand-picked glyphs where the derived one would be ambiguous (e.g. the bending family). */
const GLYPH_OVERRIDES: Record<string, string> = {
  airbend: 'Ab',
  earthbend: 'Eb',
  firebending: 'Fb',
  waterbend: 'Wb',
  warp: 'Wp',
  endure: 'En',
};

const GLYPH_STOP_WORDS = new Set(['a', 'an', 'the', 'of', 'to', 'into', 'from', 'with', 'your']);

/** First letter of the first word + first letter of the next significant word (or 2nd letter). */
function deriveKeywordGlyph(keyword: string): string {
  const words = keyword
    .toLowerCase()
    .replace(/[^a-z\s-]/g, '')
    .split(/[\s-]+/)
    .filter(Boolean);
  const [first = '', ...rest] = words;
  const second = rest.find((w) => !GLYPH_STOP_WORDS.has(w));
  const sub = second ? second.charAt(0) : first.charAt(1);
  return first.charAt(0).toUpperCase() + sub;
}

/** Greedy unique glyphs: override → derived → first letter + a later letter of the keyword → any letter. */
function assignUniqueGlyphs(keywords: readonly string[]): [string, string][] {
  const used = new Set(Object.values(GLYPH_OVERRIDES));
  return keywords.map((kw) => {
    const override = GLYPH_OVERRIDES[kw];
    if (override) return [kw, override];
    const derived = deriveKeywordGlyph(kw);
    const head = derived.charAt(0);
    const subs = [
      ...kw
        .toLowerCase()
        .replace(/[^a-z]/g, '')
        .slice(1),
      ...'abcdefghijklmnopqrstuvwxyz',
    ];
    const candidates = [derived, ...subs.filter((ch) => ch !== head.toLowerCase()).map((ch) => head + ch)];
    const glyph = candidates.find((c) => !used.has(c)) ?? derived;
    used.add(glyph);
    return [kw, glyph];
  });
}

/**
 * All supported lowercased Scryfall keywords.
 *
 * Evergreen: flying, first strike, double strike, deathtouch, defender, flash, haste,
 *   hexproof, indestructible, lifelink, menace, reach, trample, vigilance, ward.
 */
export const KEYWORD_ICON_MAP: Record<string, KeywordIconDef> = Object.fromEntries([
  ...Object.entries(MANA_FONT_KEYWORDS).map(([kw, def]) => [kw, { ...def, glyph: deriveKeywordGlyph(kw) }]),
  ...assignUniqueGlyphs(GLYPH_KEYWORDS).map(([kw, glyph]) => [kw, { glyph, category: 'other' }]),
]);

/**
 * Specific keyword → the more generic keyword Scryfall also lists for it
 * (e.g. Islandcycling cards carry Landcycling, Typecycling and Cycling too).
 */
const KEYWORD_PARENT: Record<string, string> = {
  typecycling: 'cycling',
  landcycling: 'typecycling',
  'basic landcycling': 'landcycling',
  forestcycling: 'landcycling',
  islandcycling: 'landcycling',
  mountaincycling: 'landcycling',
  plainscycling: 'landcycling',
  swampcycling: 'landcycling',
  affinitycycling: 'typecycling',
  halflingcycling: 'typecycling',
  slivercycling: 'typecycling',
  wizardcycling: 'typecycling',
  forestwalk: 'landwalk',
  islandwalk: 'landwalk',
  mountainwalk: 'landwalk',
  plainswalk: 'landwalk',
  swampwalk: 'landwalk',
  desertwalk: 'landwalk',
  'legendary landwalk': 'landwalk',
  'nonbasic landwalk': 'landwalk',
  multikicker: 'kicker',
  megamorph: 'morph',
  'commander ninjutsu': 'ninjutsu',
  'partner with': 'partner',
  'hexproof from': 'hexproof',
  'manifest dread': 'manifest',
};

/** Generic keywords implied by a more specific keyword in the same list. */
function getImpliedKeywords(lowerKeywords: Iterable<string>): Set<string> {
  const implied = new Set<string>();
  for (const kw of lowerKeywords) {
    for (let parent = KEYWORD_PARENT[kw]; parent && !implied.has(parent); parent = KEYWORD_PARENT[parent]) {
      implied.add(parent);
    }
  }
  return implied;
}

// ============================================================================
// Lookup Function
// ============================================================================

/**
 * Filter a card's keywords to those in `KEYWORD_ICON_MAP`, dropping generic keywords implied by a
 * more specific one (display only — filtering still matches the raw keyword list).
 * Returns entries sorted evergreen-first, then alphabetically within each tier.
 */
export function getKeywordIcons(keywords: string[]): KeywordIconEntry[] {
  const entries: KeywordIconEntry[] = [];
  const implied = getImpliedKeywords(keywords.map((kw) => kw.toLowerCase()));

  for (const kw of keywords) {
    const lower = kw.toLowerCase();
    const def = KEYWORD_ICON_MAP[lower];
    if (def && !implied.has(lower)) {
      entries.push({ keyword: kw, ...def });
    }
  }

  // Sort: evergreen first, then alphabetically within each tier
  entries.sort((a, b) => {
    if (a.category !== b.category) {
      return a.category === 'evergreen' ? -1 : 1;
    }
    return a.keyword.localeCompare(b.keyword);
  });

  return entries;
}
